import TablePagination from "@mui/material/TablePagination";
import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

import Skeleton from "../Skeleton";
import NoResults from "./NoResults";
import TableColumnHeadline from "./TableColumnHeadline";
import TablePaginationActions from "./TablePaginationActions";
import TableRow from "./TableRow";

import useSortableTable from "../../hooks/useSortableTable";

import "./table.scss";

const Table = ({
  className,
  columns,
  lists,
  totalCount,
  title,
  isAll,
  tableTypePlural,
  tableTypeSingular,
}) => {
  const columnHeadlines = useMemo(() => [...columns], [columns]);
  const { items, requestSort, sortConfig } = useSortableTable(lists, {
    key: "date",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  useEffect(() => {
    requestSort("date");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <table
        className={classnames("table", "table--listing", className)}
        role="grid"
      >
        <thead>
          <tr>
            {columnHeadlines?.map(
              ({ label, key, isSortable, isHidden }, index) => (
                <th
                  key={index}
                  role="columnheader"
                  scope="col"
                  aria-sort={
                    sortConfig && sortConfig.key === key
                      ? sortConfig.direction
                      : null
                  }
                >
                  <TableColumnHeadline
                    label={label}
                    name={key}
                    className={
                      sortConfig && sortConfig.key === key
                        ? sortConfig.direction
                        : ""
                    }
                    isSortable={isSortable}
                    onClick={() => {
                      requestSort(key);
                    }}
                  />
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {items
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            ?.map((row, index) => (
              <TableRow row={row} columns={columns} key={index} />
            ))}
        </tbody>
      </table>

      <Skeleton count={10} loaded={totalCount === 0 || !!totalCount}>
        {items?.length < 1 ? (
          <NoResults
            tabText={title}
            mainText={`Showing 0 ${tableTypePlural}`}
            subText={
              isAll
                ? `Once you create your first ${tableTypeSingular} you can access it here`
                : `Once a ${tableTypeSingular} is created you can access it here`
            }
          />
        ) : (
          <table>
            <tbody>
              <tr>
                <TablePagination
                  rowsPerPageOptions={[
                    25,
                    50,
                    100,
                    { label: "All", value: -1 },
                  ]}
                  colSpan={3}
                  count={items.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </tr>
            </tbody>
          </table>
        )}
      </Skeleton>
    </>
  );
};

Table.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.array,
  lists: PropTypes.array,
  totalCount: PropTypes.number,
  title: PropTypes.string,
  isAll: PropTypes.bool,
  tableTypePlural: PropTypes.string,
  tableTypeSingular: PropTypes.string,
};

Table.defaultProps = {
  columns: [],
  lists: [],
  totalCount: 0,
  title: "",
  isAll: true,
  tableTypePlural: "",
  tableTypeSingular: "",
};

export default Table;
